package com.matchpredictor.web.api

import com.matchpredictor.web.AchievementDto
import com.matchpredictor.web.AdminPasswordResetResponse
import com.matchpredictor.web.AuthResponse
import com.matchpredictor.web.ChatMessageDto
import com.matchpredictor.web.ChatStreamTicketResponse
import com.matchpredictor.web.ChangeTemporaryPasswordRequest
import com.matchpredictor.web.CreateMatchRequest
import com.matchpredictor.web.CreateNotificationCampaignRequest
import com.matchpredictor.web.CreatePollRequest
import com.matchpredictor.web.FeedSyncResponse
import com.matchpredictor.web.ForgotPasswordRequest
import com.matchpredictor.web.LeagueDto
import com.matchpredictor.web.LoginRequest
import com.matchpredictor.web.MatchDto
import com.matchpredictor.web.MessageResponse
import com.matchpredictor.web.NotificationCampaignDto
import com.matchpredictor.web.NotificationDto
import com.matchpredictor.web.NotificationListResponse
import com.matchpredictor.web.NotificationPreferencesDto
import com.matchpredictor.web.NotificationSettingsDto
import com.matchpredictor.web.PlacePredictionRequest
import com.matchpredictor.web.PollDto
import com.matchpredictor.web.PushRegisterRequest
import com.matchpredictor.web.RatingEntryDto
import com.matchpredictor.web.RegisterRequest
import com.matchpredictor.web.ResetPasswordRequest
import com.matchpredictor.web.SendChatMessageRequest
import com.matchpredictor.web.SeasonDto
import com.matchpredictor.web.SetAvatarRequest
import com.matchpredictor.web.SettlePollRequest
import com.matchpredictor.web.StickersMineResponse
import com.matchpredictor.web.TokenRequest
import com.matchpredictor.web.UnreadCountResponse
import com.matchpredictor.web.UpdateEmailRequest
import com.matchpredictor.web.UpdateNotificationPreferencesRequest
import com.matchpredictor.web.UpdateNotificationSettingsRequest
import com.matchpredictor.web.UserDto
import com.matchpredictor.web.VapidPublicKeyResponse
import kotlinx.browser.localStorage
import kotlinx.browser.window
import kotlinx.coroutines.await
import kotlinx.serialization.encodeToString
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.JsonArray
import kotlinx.serialization.json.decodeFromJsonElement
import org.w3c.fetch.Headers
import org.w3c.fetch.RequestInit
import org.w3c.fetch.Response

object ApiClient {
    private val json = Json { ignoreUnknownKeys = true; isLenient = true }

    /** Local webpack (port 3000) → backend :8080; production → same origin (nginx /api). */
    var baseUrl: String =
        when (window.location.hostname) {
            "localhost", "127.0.0.1" -> "http://localhost:8080"
            // Android emulator host loopback (Chrome on AVD opens http://10.0.2.2:3000).
            "10.0.2.2" -> "http://10.0.2.2:8080"
            else -> ""
        }

    var token: String?
        get() = localStorage.getItem("mp_token")
        set(value) {
            if (value == null) localStorage.removeItem("mp_token")
            else localStorage.setItem("mp_token", value)
        }

    /** Same-origin (prod) or backend proxy (local) so logos are not blocked. */
    fun teamLogoSrc(logoUrl: String?): String? {
        if (logoUrl.isNullOrBlank()) return null
        val id = Regex("""(?:/logos/|/api/logos/)(\d+)""").find(logoUrl)?.groupValues?.get(1)
            ?: return logoUrl
        return "$baseUrl/api/logos/$id"
    }

    /** Пропускает элементы с неизвестной лигой вместо маппинга в EPL. */
    private inline fun <reified T> decodeListSkippingBad(raw: String): List<T> {
        val element = json.parseToJsonElement(raw)
        val arr = element as? JsonArray ?: return json.decodeFromString(raw)
        return arr.mapNotNull { item ->
            runCatching { json.decodeFromJsonElement<T>(item) }.getOrNull()
        }
    }

    private suspend fun performRequest(method: String, path: String, body: String?): Response {
        val headers = Headers()
        headers.append("Content-Type", "application/json")
        token?.let { headers.append("Authorization", "Bearer $it") }

        val init = RequestInit(
            method = method,
            headers = headers,
            body = body
        )
        return try {
            window.fetch("$baseUrl$path", init).await()
        } catch (_: Throwable) {
            throw IllegalStateException("Не удалось связаться с сервером. Проверьте подключение к интернету")
        }
    }

    private suspend fun request(method: String, path: String, body: String? = null): String {
        var response = performRequest(method, path, body)
        var retriedWithoutExpiredToken = false
        if (response.status.toInt() == 401 && token != null) {
            token = null
            retriedWithoutExpiredToken = true
            response = performRequest(method, path, body)
        }
        if (
            response.status.toInt() == 401 &&
            retriedWithoutExpiredToken &&
            path != "/api/auth/login" &&
            path != "/api/auth/register" &&
            path != "/api/auth/forgot-password" &&
            path != "/api/auth/reset-password" &&
            path != "/api/auth/verify-email"
        ) {
            window.location.reload()
            throw IllegalStateException("Сессия истекла. Войдите снова")
        }
        val text = response.text().await()
        if (!response.ok) {
            val message = runCatching { json.decodeFromString<MessageResponse>(text).message }
                .getOrElse { text.ifBlank { "Ошибка сервера: HTTP ${response.status}" } }
            throw IllegalStateException(message)
        }
        return text
    }

    suspend fun login(username: String, password: String): AuthResponse {
        val text = request("POST", "/api/auth/login", json.encodeToString(LoginRequest(username, password)))
        return json.decodeFromString(text)
    }

    suspend fun register(username: String, password: String, displayName: String, email: String): AuthResponse {
        val text = request(
            "POST",
            "/api/auth/register",
            json.encodeToString(RegisterRequest(username, password, displayName, email))
        )
        return json.decodeFromString(text)
    }

    suspend fun verifyEmail(token: String): MessageResponse =
        json.decodeFromString(
            request("POST", "/api/auth/verify-email", json.encodeToString(TokenRequest(token)))
        )

    suspend fun resendVerification(): MessageResponse =
        json.decodeFromString(request("POST", "/api/auth/resend-verification"))

    suspend fun updateEmail(email: String, currentPassword: String): UserDto =
        json.decodeFromString(
            request("PUT", "/api/auth/email", json.encodeToString(UpdateEmailRequest(email, currentPassword)))
        )

    suspend fun forgotPassword(email: String): MessageResponse =
        json.decodeFromString(
            request("POST", "/api/auth/forgot-password", json.encodeToString(ForgotPasswordRequest(email)))
        )

    suspend fun resetPassword(token: String, newPassword: String): MessageResponse =
        json.decodeFromString(
            request(
                "POST",
                "/api/auth/reset-password",
                json.encodeToString(ResetPasswordRequest(token, newPassword))
            )
        )

    suspend fun me(): UserDto = json.decodeFromString(request("GET", "/api/auth/me"))

    suspend fun changeTemporaryPassword(newPassword: String): AuthResponse =
        json.decodeFromString(
            request(
                "POST",
                "/api/auth/change-temporary-password",
                json.encodeToString(ChangeTemporaryPasswordRequest(newPassword))
            )
        )

    suspend fun adminUsers(): List<UserDto> =
        json.decodeFromString(request("GET", "/api/auth/admin/users"))

    suspend fun resetUserPassword(userId: Long): AdminPasswordResetResponse =
        json.decodeFromString(request("POST", "/api/auth/admin/users/$userId/reset-password"))

    suspend fun chatHistory(beforeId: Long? = null, limit: Int = 50): List<ChatMessageDto> {
        val params = mutableListOf("limit=$limit")
        beforeId?.let { params += "beforeId=$it" }
        return json.decodeFromString(request("GET", "/api/chat/messages?${params.joinToString("&")}"))
    }

    suspend fun sendChatMessage(body: String?, replyToId: Long?, stickerCode: String? = null): ChatMessageDto =
        json.decodeFromString(
            request(
                "POST",
                "/api/chat/messages",
                json.encodeToString(SendChatMessageRequest(body, stickerCode, replyToId))
            )
        )

    suspend fun stickersMine(): StickersMineResponse =
        json.decodeFromString(request("GET", "/api/stickers/mine"))

    suspend fun chatStreamTicket(): ChatStreamTicketResponse =
        json.decodeFromString(request("POST", "/api/chat/stream-ticket"))

    fun chatStreamUrl(ticket: String): String =
        "$baseUrl/api/chat/stream?ticket=$ticket"

    suspend fun polls(status: String? = null, league: String? = null): List<PollDto> {
        val params = mutableListOf<String>()
        status?.let { params += "status=$it" }
        league?.let { params += "league=$it" }
        val q = if (params.isEmpty()) "" else "?${params.joinToString("&")}"
        return decodeListSkippingBad(request("GET", "/api/polls$q"))
    }

    suspend fun getPoll(id: Long): PollDto =
        json.decodeFromString(request("GET", "/api/polls/$id"))

    suspend fun leagues(): List<LeagueDto> =
        json.decodeFromString<List<LeagueDto>>(request("GET", "/api/leagues"))
            .sortedBy { it.title }

    suspend fun syncFeed(): FeedSyncResponse =
        json.decodeFromString(request("POST", "/api/feed/sync"))

    suspend fun feedStatus(): MessageResponse =
        json.decodeFromString(request("GET", "/api/feed/status"))

    suspend fun placePrediction(pollId: Long, choice: com.matchpredictor.web.MatchOutcome) {
        request("POST", "/api/predictions", json.encodeToString(PlacePredictionRequest(pollId, choice)))
    }

    suspend fun ratings(
        seasonId: Long? = null,
        league: String? = null,
        round: Int? = null
    ): List<RatingEntryDto> {
        val params = mutableListOf<String>()
        seasonId?.let { params += "seasonId=$it" }
        league?.let { params += "league=$it" }
        round?.let { params += "round=$it" }
        val q = if (params.isEmpty()) "" else "?${params.joinToString("&")}"
        return json.decodeFromString(request("GET", "/api/ratings$q"))
    }

    suspend fun achievements(): List<AchievementDto> =
        json.decodeFromString(request("GET", "/api/achievements/mine"))

    suspend fun setAvatar(code: String): UserDto =
        json.decodeFromString(request("POST", "/api/achievements/avatar", json.encodeToString(SetAvatarRequest(code))))

    suspend fun activeSeason(): SeasonDto =
        json.decodeFromString(request("GET", "/api/seasons/active"))

    suspend fun matches(seasonId: Long? = null): List<MatchDto> {
        val q = seasonId?.let { "?seasonId=$it" } ?: ""
        return decodeListSkippingBad(request("GET", "/api/matches$q"))
    }

    suspend fun createMatch(req: CreateMatchRequest): MatchDto =
        json.decodeFromString(request("POST", "/api/matches", json.encodeToString(req)))

    suspend fun createPoll(matchId: Long): PollDto =
        json.decodeFromString(request("POST", "/api/polls", json.encodeToString(CreatePollRequest(matchId))))

    suspend fun settlePoll(pollId: Long, result: com.matchpredictor.web.MatchOutcome): PollDto =
        json.decodeFromString(
            request("POST", "/api/polls/$pollId/settle", json.encodeToString(SettlePollRequest(result)))
        )

    suspend fun notifications(limit: Int = 30, beforeId: Long? = null): NotificationListResponse {
        val params = mutableListOf("limit=$limit")
        beforeId?.let { params += "beforeId=$it" }
        return json.decodeFromString(request("GET", "/api/notifications?${params.joinToString("&")}"))
    }

    suspend fun unreadNotificationCount(): UnreadCountResponse =
        json.decodeFromString(request("GET", "/api/notifications/unread-count"))

    suspend fun markNotificationRead(id: Long) {
        request("POST", "/api/notifications/$id/read")
    }

    suspend fun markAllNotificationsRead() {
        request("POST", "/api/notifications/read-all")
    }

    suspend fun notificationPreferences(): NotificationPreferencesDto =
        json.decodeFromString(request("GET", "/api/notifications/preferences"))

    suspend fun updateNotificationPreferences(req: UpdateNotificationPreferencesRequest): NotificationPreferencesDto =
        json.decodeFromString(
            request("PUT", "/api/notifications/preferences", json.encodeToString(req))
        )

    suspend fun vapidPublicKey(): VapidPublicKeyResponse =
        json.decodeFromString(request("GET", "/api/push/vapid-public-key"))

    suspend fun registerPush(req: PushRegisterRequest) {
        request("POST", "/api/push/register", json.encodeToString(req))
    }

    suspend fun adminNotificationSettings(): NotificationSettingsDto =
        json.decodeFromString(request("GET", "/api/admin/notifications/settings"))

    suspend fun updateAdminNotificationSettings(req: UpdateNotificationSettingsRequest): NotificationSettingsDto =
        json.decodeFromString(
            request("PUT", "/api/admin/notifications/settings", json.encodeToString(req))
        )

    suspend fun adminNotificationCampaigns(): List<NotificationCampaignDto> =
        json.decodeFromString(request("GET", "/api/admin/notifications/campaigns"))

    suspend fun createNotificationCampaign(req: CreateNotificationCampaignRequest): NotificationCampaignDto =
        json.decodeFromString(
            request("POST", "/api/admin/notifications/campaigns", json.encodeToString(req))
        )

    suspend fun startNotificationCampaign(id: Long): NotificationCampaignDto =
        json.decodeFromString(request("POST", "/api/admin/notifications/campaigns/$id/start"))

    suspend fun pauseNotificationCampaign(id: Long): NotificationCampaignDto =
        json.decodeFromString(request("POST", "/api/admin/notifications/campaigns/$id/pause"))

    suspend fun cancelNotificationCampaign(id: Long): NotificationCampaignDto =
        json.decodeFromString(request("POST", "/api/admin/notifications/campaigns/$id/cancel"))
}
